import { Alert, Card, Flex, Layout, Spin, Typography } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"

import { openSigningLink } from "./api/signing.services"
import ConsentStep from "./components/ConsentStep"
import ConsumerRights from "./components/ConsumerRights"
import DocumentStep from "./components/DocumentStep"
import IdentityStep from "./components/IdentityStep"
import RefusedCard from "./components/RefusedCard"
import { useSigningDocument } from "./hooks/use-signing-document"
import type { OpenedLink, SigningRefusal } from "./types/signing.types"

const { Title, Text } = Typography

type Stage = "opening" | "code" | "consent" | "document" | "refused"

/**
 * The page a recipient with no EZBuild account lands on.
 *
 * The token arrives in the URL fragment and is read once, synchronously, then
 * wiped from the address bar: a fragment never reaches a server, but it does sit
 * in history and in anything that reports the current URL.
 *
 * The session it is exchanged for is held in memory only. Putting it in storage
 * would leave a working key to somebody's contract on a shared computer long
 * after they had walked away.
 */
const SigningPage: React.FC = () => {
  const { t } = useTranslation()
  const [stage, setStage] = useState<Stage>("opening")
  const [opened, setOpened] = useState<OpenedLink | null>(null)
  const [session, setSession] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<SigningRefusal | null>(null)
  // The fragment, watched rather than read once. Opening a second link in the
  // same tab changes nothing but the hash, so the route never changes and the
  // page would otherwise keep showing the first recipient's document — and
  // their session with it.
  const { hash } = useLocation()
  const opening = useRef<string | null>(null)

  // Nothing this page requests may carry where it came from. The fragment is
  // already stripped from the address bar below, but a referrer would still name
  // /sign to every font, script and image host the browser talks to, and for a
  // page somebody was sent privately that is one leak too many.
  useEffect(() => {
    const meta = document.createElement("meta")
    meta.name = "referrer"
    meta.content = "no-referrer"
    document.head.appendChild(meta)
    return () => {
      document.head.removeChild(meta)
    }
  }, [])

  useEffect(() => {
    const token = hash.replace(/^#/, "")

    // Strict mode mounts twice in development, and clearing the fragment below
    // re-runs this effect; opening a link twice would record two openings for
    // one person walking in once.
    if (!token || opening.current === token) {
      if (!token && opening.current === null) {
        setRefusal({ reason: "unknown" })
        setStage("refused")
      }
      return
    }
    opening.current = token
    setStage("opening")
    setOpened(null)
    setSession(null)
    window.history.replaceState(null, "", window.location.pathname)

    void openSigningLink(token)
      .then((response) => {
        if (!response.ok || !response.data) {
          setRefusal(response.refusal)
          setStage("refused")
          return
        }
        setOpened(response.data)
        setSession(response.data.session)
        setStage(response.data.next === "code" ? "code" : "document")
      })
      // A network that never answers would otherwise leave the spinner turning
      // with nothing to read and nothing to try.
      .catch(() => {
        setRefusal({ reason: "unknown" })
        setStage("refused")
      })
  }, [hash])

  const onIdentified = useCallback(
    (next: string) => {
      setSession(next)
      // Only a signer is asked to agree to anything. A viewer proves the inbox
      // and then reads the document: consent belongs before a signing field,
      // and they are never shown one.
      setStage(opened?.recipient.role === "viewer" ? "document" : "consent")
    },
    [opened]
  )

  const onRefused = useCallback((next: SigningRefusal) => {
    setRefusal(next)
    setStage("refused")
  }, [])

  // Fetched here rather than in each step: every fetch is a recorded opening,
  // and the disclosure and the document both show the same file.
  const wantsDocument = stage === "consent" || stage === "document"
  const pdf = useSigningDocument(session, wantsDocument)

  useEffect(() => {
    if (pdf.refusal) onRefused(pdf.refusal)
  }, [pdf.refusal, onRefused])

  return (
    <Layout className="min-h-svh auth-bg">
      <Flex
        vertical
        align="center"
        justify="center"
        className="min-h-svh"
        style={{ padding: "16px" }}
      >
        <Card style={{ width: "100%", maxWidth: 640 }}>
          {opened && stage !== "refused" && (
            <Flex vertical gap={4} style={{ marginBottom: 24 }}>
              <Title level={4} style={{ margin: 0 }}>
                {opened.document_name}
              </Title>
              <Text type="secondary">
                {t("signing.addressedTo", { name: opened.recipient.name })}
              </Text>
            </Flex>
          )}

          {stage === "opening" && (
            <Flex justify="center" style={{ padding: "48px 0" }}>
              <Spin size="large" />
            </Flex>
          )}

          {stage === "code" && session && (
            <IdentityStep
              session={session}
              onIdentified={onIdentified}
              onRefused={onRefused}
            />
          )}

          {stage === "consent" && session && (
            <ConsentStep
              session={session}
              documentUrl={pdf.url}
              onAgreed={() => setStage("document")}
              onRefused={onRefused}
            />
          )}

          {stage === "document" && session && opened && (
            <DocumentStep
              session={session}
              documentUrl={pdf.url}
              documentBlob={pdf.blob}
              documentName={opened.document_name}
              recipientName={opened.recipient.name}
              isSigner={opened.recipient.role === "signer"}
            />
          )}

          {wantsDocument && session && opened && (
            <ConsumerRights
              session={session}
              isSigner={opened.recipient.role === "signer"}
              onWithdrawn={onRefused}
            />
          )}

          {stage === "refused" && <RefusedCard refusal={refusal} />}
        </Card>

        {stage !== "refused" && (
          <Alert
            type="info"
            showIcon
            style={{ maxWidth: 640, marginTop: 16 }}
            message={t("signing.linkIsPersonal")}
          />
        )}
      </Flex>
    </Layout>
  )
}

export default SigningPage
